import React, { useMemo, useState } from 'react';
import { Plus, Users, Trash2, X, Pencil } from 'lucide-react';
import { loadDevJson, saveDevJson } from '../../lib/devStorage';
import { PageSearchBar } from '../common/PageSearchBar';
import { mediaSrc } from '../../lib/mediaUrl';

const CAP_KEY = 'cap_table_owners_v1';

export type CapTableOwner = {
  id: string;
  name: string;
  contact: string;
  share: string;
  image: string;
};

function readOwners(): CapTableOwner[] {
  return loadDevJson<CapTableOwner[]>(CAP_KEY, []);
}

function writeOwners(list: CapTableOwner[]) {
  saveDevJson(CAP_KEY, list);
}

function ownerInitials(name: string) {
  const p = name.trim().split(/\s+/).filter(Boolean);
  if (!p.length) return '?';
  if (p.length === 1) return p[0].slice(0, 2).toUpperCase();
  return (p[0][0] + p[p.length - 1][0]).toUpperCase();
}

const OwnersSettings: React.FC = () => {
  const [owners, setOwners] = useState<CapTableOwner[]>(() => readOwners());
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({ name: '', contact: '', share: '', image: '' });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return owners;
    return owners.filter((o) => [o.name, o.contact, o.share].join(' ').toLowerCase().includes(q));
  }, [owners, search]);

  const openCreate = () => {
    setEditingId(null);
    setForm({ name: '', contact: '', share: '', image: '' });
    setShowModal(true);
  };

  const openEdit = (o: CapTableOwner) => {
    setEditingId(o.id);
    setForm({ name: o.name, contact: o.contact, share: o.share, image: o.image || '' });
    setShowModal(true);
  };

  const persist = (next: CapTableOwner[]) => {
    setOwners(next);
    writeOwners(next);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.contact.trim() || !form.share.trim()) return;
    if (editingId) {
      persist(owners.map((o) => (o.id === editingId ? { ...o, ...form } : o)));
    } else {
      persist([{ id: crypto.randomUUID(), ...form }, ...owners]);
    }
    setShowModal(false);
  };

  const removeOwner = (id: string) => {
    if (!window.confirm('Remove this owner from the cap table?')) return;
    persist(owners.filter((o) => o.id !== id));
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2 text-zinc-900">
          <Users className="h-5 w-5 shrink-0 text-emerald-700" aria-hidden />
          <p className="text-sm text-zinc-600">Partners on the cap table and their contact points (saved per workspace).</p>
        </div>
        <button type="button" onClick={openCreate} className="app-btn-primary inline-flex shrink-0 items-center gap-2">
          <Plus className="h-4 w-4" />
          Add owner
        </button>
      </div>

      <PageSearchBar value={search} onChange={setSearch} placeholder="Search owners…" className="max-w-full" />

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {filtered.map((owner) => (
          <div
            key={owner.id}
            className="flex items-center gap-3 rounded-md border border-zinc-200 bg-white p-4 shadow-sm transition-shadow hover:border-zinc-300 hover:shadow-md"
          >
            {owner.image ? (
              <img src={mediaSrc(owner.image)} alt="" className="h-14 w-14 shrink-0 rounded-full object-cover ring-1 ring-zinc-200" />
            ) : (
              <div
                className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-zinc-200 text-sm font-semibold text-zinc-700 ring-1 ring-zinc-200"
                aria-hidden
              >
                {ownerInitials(owner.name)}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <h3 className="truncate text-sm font-semibold text-zinc-900">{owner.name}</h3>
              <p className="truncate text-xs text-zinc-500">{owner.contact}</p>
              <p className="mt-1 text-xs font-semibold text-emerald-800">Share {owner.share}%</p>
            </div>
            <div className="flex shrink-0 gap-1">
              <button
                type="button"
                onClick={() => openEdit(owner)}
                className="rounded-md p-2 text-zinc-600 hover:bg-zinc-100"
                aria-label={`Edit ${owner.name}`}
              >
                <Pencil className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => removeOwner(owner.id)}
                className="shrink-0 rounded-md p-2 text-rose-600 hover:bg-rose-50"
                aria-label={`Remove ${owner.name}`}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {owners.length === 0 && (
        <p className="rounded-md border border-dashed border-zinc-200 py-8 text-center text-sm text-zinc-500">
          No owners yet. Use &quot;Add owner&quot; to create entries.
        </p>
      )}

      {owners.length > 0 && filtered.length === 0 && (
        <p className="text-center text-sm text-zinc-500">No matches for your search.</p>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-950/40 p-4 backdrop-blur-sm">
          <div className="app-card max-h-[90vh] w-full max-w-lg overflow-y-auto shadow-lg">
            <div className="flex items-center justify-between border-b border-zinc-200 px-4 py-3 sm:px-5">
              <h3 className="text-sm font-semibold text-zinc-900">{editingId ? 'Edit owner' : 'Add owner'}</h3>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="rounded-md p-1 text-zinc-500 hover:bg-zinc-100 hover:text-zinc-800"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="app-card-inner space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div className="sm:col-span-3">
                  <label className="app-label">Owner name</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="app-field"
                    placeholder="Full name"
                    required
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="app-label">Contact</label>
                  <input
                    type="tel"
                    value={form.contact}
                    onChange={(e) => setForm({ ...form, contact: e.target.value })}
                    className="app-field"
                    placeholder="Phone"
                    required
                  />
                </div>
                <div>
                  <label className="app-label">Share %</label>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={form.share}
                    onChange={(e) => setForm({ ...form, share: e.target.value })}
                    className="app-field"
                    placeholder="0–100"
                    required
                  />
                </div>
                <div className="sm:col-span-3">
                  <label className="app-label">Avatar URL (optional)</label>
                  <input
                    type="url"
                    value={form.image}
                    onChange={(e) => setForm({ ...form, image: e.target.value })}
                    className="app-field"
                    placeholder="https://…"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 border-t border-zinc-100 pt-4">
                <button type="button" onClick={() => setShowModal(false)} className="app-btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="app-btn-primary">
                  {editingId ? 'Save' : 'Add owner'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default OwnersSettings;
